import {
  Box,
  TablePagination,
  TablePaginationProps,
  Typography,
  useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import React from "react";
import { useTranslation } from "react-i18next";
import { Action, AtonUnitDetails } from "~/api/GeneratedApi";
import GenericTable, {
  CustomColumn,
  CustomTableProps,
} from "~/components/GenericTable";
import PaddedBox from "~/components/PaddedBox";

type Props = {
  small?: boolean;
} & (
  | {
      atonData?: never;
      actionData: Action[];
    }
  | {
      atonData: AtonUnitDetails;
      actionData?: never;
    }
);

const AtoNActionsTab = ({
  atonData,
  small,
  actionData,
}: Props): JSX.Element => {
  const { t } = useTranslation("aton-action");

  const theme = useTheme();

  const actions = atonData?.actions ?? actionData;

  const columns: CustomColumn<Action>[] = React.useMemo(
    () => [
      {
        title: t("date"),
        field: "timestamp",
        render: ({ timestamp }) => {
          return timestamp
            ? dayjs(timestamp).format("DD/MM/YYYY HH:mm:ss")
            : "";
        },
      },
      {
        title: t("state"),
        field: "state",
      },
      {
        title: t("type"),
        field: "type",
      },
      {
        title: t("atonUnit"),
        field: "atonUnit",
        render: ({ atonUnit }) => {
          if (!atonUnit) return "";
          const { type, atonNumber, name } = atonUnit;
          return (
            <Box>
              <Typography>{`${atonNumber} ${name}`}</Typography>
              <Typography>{type}</Typography>
            </Box>
          );
        },
      },
      {
        title: t("information"),
        field: "memo",
      },
    ],
    [t],
  );

  const tableProps: CustomTableProps<Action> = React.useMemo(
    () => ({
      title: t("aton:list-actions"),
      ...(small
        ? {
            options: {
              pageSizeOptions: [5, 10, 15, 25, 50],
              pageSize: 10,
              // headerStyle: { marginLeft: "45px" },
            },
            components: {
              Container: (props) => (
                <div
                  {...props}
                  style={{
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                    border: "none",
                  }}
                />
              ),
              Pagination: (props: TablePaginationProps) => {
                const { style } = props;
                return (
                  <TablePagination
                    {...props}
                    style={{
                      borderBottomRightRadius: theme.shape.borderRadius,
                      ...style,
                    }}
                  />
                );
              },
            },
          }
        : {}),
    }),
    [small, t, theme.shape.borderRadius],
  );

  const table = React.useMemo(
    () => (
      <GenericTable
        data={actions ?? []}
        columns={columns}
        tableProps={tableProps}
      />
    ),
    [actions, columns, tableProps],
  );

  return small ? table : <PaddedBox>{table}</PaddedBox>;
};

export default AtoNActionsTab;
